import React, { useState, useRef } from 'react';
import { motion } from 'framer-motion';
import { Play, Pause, Volume2, VolumeX, Rewind, FastForward } from 'lucide-react';

const PodcastSection = () => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [progress, setProgress] = useState(0);
  const audioRef = useRef(null); // Reference to the audio element

  const sampleEpisode = {
    title: "The Mystery of the Vanishing Cookies",
    duration: "04:37",
    description: "Join Ned and Nancy in their first audio adventure as they uncover the secrets behind mysterious vanishing cookies!",
    audioSrc: "/audio/podcast.mp3", // Path to the podcast audio
  };

  const togglePlayPause = () => {
    if (isPlaying) {
      audioRef.current.pause();
    } else {
      audioRef.current.play();
    }
    setIsPlaying(!isPlaying);
  };

  const handleMute = () => {
    const audio = audioRef.current;
    audio.muted = !audio.muted;
    setIsMuted(!isMuted);
  };

  return (
    <div className="relative py-20 overflow-hidden">
      {/* Matching Background */}
      <div className="absolute inset-0 bg-gradient-to-b from-slate-900 to-blue-900">
        <div className="absolute inset-0" style={{ opacity: 0.1 }}>
          {[...Array(20)].map((_, i) => (
            <motion.div
              key={i}
              className="absolute h-2 w-2 bg-yellow-300 rounded-full"
              style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
              }}
              animate={{
                scale: [1, 1.5, 1],
                opacity: [0.3, 0.8, 0.3],
              }}
              transition={{
                duration: 2 + Math.random() * 2,
                repeat: Infinity,
                repeatType: "reverse",
              }}
            />
          ))}
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 relative">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          className="text-center mb-16"
        >
          <h2 className="text-5xl font-bold text-white mb-4">Ned & Nancy Podcast</h2>
          <p className="text-blue-200 text-xl">Listen to their adventures come alive!</p>
        </motion.div>

        {/* Player Card */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          whileInView={{ opacity: 1, scale: 1 }}
          className="bg-white/10 backdrop-blur-lg rounded-2xl p-8 shadow-2xl max-w-3xl mx-auto mb-12"
        >
          {/* Episode Info */}
          <div className="flex items-center gap-6 mb-8">
            <div className="w-24 h-24 rounded-lg overflow-hidden flex-shrink-0">
              <img 
                src="/images/episode.webp" 
                alt="Episode Cover" 
                className="w-full h-full object-cover"
              />
            </div>
            <div>
              <h3 className="text-2xl font-bold text-white mb-2">{sampleEpisode.title}</h3>
              <p className="text-blue-200">{sampleEpisode.description}</p>
            </div>
          </div>

          {/* Audio Player */}
          <div className="space-y-4">
            <audio
              ref={audioRef}
              src={sampleEpisode.audioSrc}
              onTimeUpdate={() =>
                setProgress((audioRef.current.currentTime / audioRef.current.duration) * 100)
              }
            />
            <div className="relative h-1 bg-white/20 rounded-full">
              <motion.div 
                className="absolute left-0 top-0 h-full bg-blue-400 rounded-full"
                style={{ width: `${progress}%` }}
              />
              <input 
                type="range" 
                min="0" 
                max="100" 
                value={progress}
                onChange={(e) => {
                  const newProgress = e.target.value;
                  setProgress(newProgress);
                  audioRef.current.currentTime =
                    (newProgress / 100) * audioRef.current.duration;
                }}
                className="absolute inset-0 opacity-0 cursor-pointer"
              />
            </div>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <button 
                  className="text-white hover:text-blue-300 transition-colors"
                  onClick={() => {
                    audioRef.current.currentTime = Math.max(0, audioRef.current.currentTime - 10);
                  }}
                >
                  <Rewind size={24} />
                </button>
                <button 
                  className="w-12 h-12 rounded-full bg-blue-400 hover:bg-blue-300 transition-colors flex items-center justify-center text-slate-900"
                  onClick={togglePlayPause}
                >
                  {isPlaying ? <Pause size={24} /> : <Play size={24} />}
                </button>
                <button 
                  className="text-white hover:text-blue-300 transition-colors"
                  onClick={() => {
                    audioRef.current.currentTime = Math.min(
                      audioRef.current.duration,
                      audioRef.current.currentTime + 10
                    );
                  }}
                >
                  <FastForward size={24} />
                </button>
              </div>
              <div className="flex items-center gap-4">
                <button 
                  className="text-white hover:text-blue-300 transition-colors"
                  onClick={handleMute}
                >
                  {isMuted ? <VolumeX size={24} /> : <Volume2 size={24} />}
                </button>
                <span className="text-white">{sampleEpisode.duration}</span>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Subscription Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          className="bg-white/5 backdrop-blur-sm rounded-xl p-8 max-w-2xl mx-auto text-center"
        >
          <h3 className="text-3xl font-bold text-white mb-4">Subscribe for More Adventures!</h3>
          <p className="text-blue-200 mb-6">
            Get exclusive access to weekly episodes, behind-the-scenes content, and interactive mysteries!
          </p>
          <div className="space-y-4">
            <p className="text-2xl font-bold text-yellow-400">$4.99/month</p>
            <button 
              onClick={() => window.open('https://apple.com/podcasts', '_blank')}
              className="bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-400 hover:to-blue-500 text-white font-bold py-4 px-8 rounded-full inline-flex items-center gap-2 transform transition hover:scale-105"
            >
              <img src="/images/apple.png" alt="Apple Podcasts" className="w-6 h-6" />
              Subscribe on Apple Podcasts
            </button>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default PodcastSection;
